import { MessageResponse } from '@acme/api';
import {
  HttpTransportType,
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from '@microsoft/signalr';

class SignalRService {
  private connection: HubConnection | null = null;
  private messageCallbacks: ((message: MessageResponse) => void)[] = [];
  private connectionCallbacks: ((connected: boolean) => void)[] = [];
  private debugCallbacks: ((data: any) => void)[] = [];
  private isStarting = false;
  private token: string | null = null;

  private logDebug(message: string, data?: any) {
    console.log(`[SignalR Debug] ${message}`, data || '');
    this.debugCallbacks.forEach((callback) => callback({ message, data }));
  }

  async connect(hubUrl: string, token: string): Promise<void> {
    this.token = token || null;
    // Если уже подключаемся или подключены, выходим
    if (
      this.isStarting ||
      this.connection?.state === HubConnectionState.Connected
    ) {
      this.logDebug('Connection already in progress or established');
      return;
    }

    // Если соединение существует, но не подключено, останавливаем его
    if (
      this.connection &&
      this.connection.state !== HubConnectionState.Disconnected
    ) {
      await this.cleanupConnection();
    }

    this.isStarting = true;

    try {
      this.logDebug('Creating new connection to', hubUrl);

      this.connection = new HubConnectionBuilder()
        .withUrl(hubUrl, {
          skipNegotiation: true,
          transport: HttpTransportType.WebSockets,
          // accessTokenFactory: token ? () => token : undefined,
        })
        .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
        .configureLogging(LogLevel.Warning)
        .build();

      // Обработчик входящих сообщений
      this.connection.on('ReceiveMessage', (message) => {
        this.logDebug('Received message from server', message);
        this.messageCallbacks.forEach((callback) => callback(message));
        console.log('SignalR: Received message from server', message);
      });

      // Обработчики состояния подключения
      this.connection.onreconnecting((error) => {
        this.logDebug('Connection reconnecting', error);
        this.connectionCallbacks.forEach((callback) => callback(false));
        console.log('SignalR: Connection reconnecting', error);
      });

      this.connection.onreconnected(async (connectionId) => {
        this.logDebug('Connection reconnected', connectionId);
        // При реконнекте снова авторизуем соединение
        if (this.token) {
          try {
            await this.authorizeConnection(this.token);
            this.logDebug('SignalR: Re-authorized after reconnect');
          } catch (error) {
            this.logDebug(
              'SignalR: Failed to re-authorize after reconnect:',
              error
            );
          }
        }
        this.connectionCallbacks.forEach((callback) => callback(true));
      });

      this.connection.onclose((error) => {
        this.logDebug('Connection closed', error);
        this.connectionCallbacks.forEach((callback) => callback(false));
        this.isStarting = false;
      });

      this.logDebug('Starting connection...');
      await this.connection.start();

      // Авторизуем соединение после подключения
      if (this.token) {
        await this.authorizeConnection(this.token);
        console.log('SignalR: Connection authorized');
      }

      this.logDebug('Connection established successfully');
      this.connectionCallbacks.forEach((callback) => callback(true));
      this.isStarting = false;
    } catch (error) {
      this.isStarting = false;
      this.logDebug('Connection failed', error);

      // Очищаем неудачное соединение
      await this.cleanupConnection();
      throw error;
    }
  }

  private async authorizeConnection(token: string): Promise<void> {
    if (!this.connection) {
      throw new Error('SignalR connection not established');
    }

    try {
      await this.connection.invoke('AuthorizeConnection', token);
      console.log('SignalR: AuthorizeConnection invoked successfully');
    } catch (error) {
      console.error('SignalR: AuthorizeConnection failed', error);
      throw error;
    }
  }

  // Метод для обновления токена (например, при его обновлении)
  public async updateToken(newToken: string): Promise<void> {
    this.token = newToken;

    // Если соединение активно, переавторизуемся
    if (this.connection && this.connection.state === 'Connected') {
      try {
        await this.authorizeConnection(newToken);
        console.log('SignalR: Token updated and re-authorized');
      } catch (error) {
        console.error('SignalR: Failed to re-authorize with new token:', error);
      }
    }
  }

  async disconnect(): Promise<void> {
    this.logDebug('Disconnecting...');
    await this.cleanupConnection();
  }

  private async cleanupConnection(): Promise<void> {
    if (!this.connection) return;

    try {
      // Останавливаем только если соединение активно
      if (this.connection.state === HubConnectionState.Connected) {
        await this.connection.stop();
      }

      this.connection = null;
      this.logDebug('Connection cleaned up');
    } catch (error) {
      console.error('Error during connection cleanup:', error);
    } finally {
      this.isStarting = false;
      this.connectionCallbacks.forEach((callback) => callback(false));
      this.messageCallbacks = [];
    }
  }

  // Получаем текущее состояние соединения
  getConnectionState(): string {
    return this.connection?.state || HubConnectionState.Disconnected;
  }

  onMessage(callback: (message: MessageResponse) => void): void {
    this.messageCallbacks.push(callback);
  }

  onConnectionChange(callback: (connected: boolean) => void): void {
    this.connectionCallbacks.push(callback);
  }

  onDebug(callback: (data: any) => void): void {
    this.debugCallbacks.push(callback);
  }

  removeMessageCallback(callback: (message: MessageResponse) => void): void {
    this.messageCallbacks = this.messageCallbacks.filter(
      (cb) => cb !== callback
    );
  }

  removeConnectionCallback(callback: (connected: boolean) => void): void {
    this.connectionCallbacks = this.connectionCallbacks.filter(
      (cb) => cb !== callback
    );
  }

  removeDebugCallback(callback: (data: any) => void): void {
    this.debugCallbacks = this.debugCallbacks.filter((cb) => cb !== callback);
  }
}

export const signalRService = new SignalRService();
